'use client';

import * as React from 'react';
import { useTransition } from 'react';
import { Loader2Icon, FilterIcon, CalculatorIcon, RefreshCwIcon } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@workspace/ui/components/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@workspace/ui/components/tabs';
import { Button } from '@workspace/ui/components/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@workspace/ui/components/sheet';

import { ExplorerFilters, type FilterState } from './explorer-filters';
import { TotalSpendHistogram } from './total-spend-histogram';
import { CategoryBreakdownChart } from './category-breakdown-chart';
import { ItemDistributionChart } from './item-distribution-chart';
import { StatCards } from './stat-cards';
import { WeddingListModal } from './wedding-list-modal';
import { fetchPublicWeddingSamples } from '~/app/budget-explorer/actions';
import { type WeddingSamplesResult, type WeddingSampleDto } from '~/data/budget-explorer/get-public-wedding-samples';

interface BudgetExplorerProps {
  initialData: WeddingSamplesResult;
  regions: { region: string; location: string; count: number }[];
  itemNames: string[];
}

const DEFAULT_FILTERS: FilterState = {
  radius: 100,
};

export function BudgetExplorer({
  initialData,
  regions,
  itemNames,
}: BudgetExplorerProps): React.JSX.Element {
  const [data, setData] = React.useState<WeddingSamplesResult>(initialData);
  const [isPending, startTransition] = useTransition();
  const [selectedItem, setSelectedItem] = React.useState<string>(itemNames[0] || 'Photographer');
  const [mobileFiltersOpen, setMobileFiltersOpen] = React.useState(false);
  const [currentFilters, setCurrentFilters] = React.useState<FilterState>(DEFAULT_FILTERS);
  const [hasCalculated, setHasCalculated] = React.useState(true);
  const [filtersChanged, setFiltersChanged] = React.useState(false);
  
  // Wedding drill-down state
  const [weddingModalOpen, setWeddingModalOpen] = React.useState(false);
  const [selectedBucket, setSelectedBucket] = React.useState<{
    min: number;
    max: number;
    samples: WeddingSampleDto[];
  } | null>(null);

  const handleFilterChange = React.useCallback((filters: FilterState) => {
    setCurrentFilters(filters);
    setFiltersChanged(true);
  }, []);

  const handleCalculate = React.useCallback(() => {
    startTransition(async () => {
      const result = await fetchPublicWeddingSamples({
        region: currentFilters.region,
        maxBudget: currentFilters.maxBudget,
        minBudget: currentFilters.minBudget,
        minGuestCount: currentFilters.minGuestCount,
        maxGuestCount: currentFilters.maxGuestCount,
      });
      setData(result);
      setHasCalculated(true);
      setFiltersChanged(false);
    });
  }, [currentFilters]);

  const handleReset = React.useCallback(() => {
    setData(initialData);
    setCurrentFilters(DEFAULT_FILTERS);
    setHasCalculated(true);
    setFiltersChanged(false);
  }, [initialData]);

  const handleBucketClick = React.useCallback(
    (bucket: { min: number; max: number; samples: WeddingSampleDto[] }) => {
      setSelectedBucket(bucket);
      setWeddingModalOpen(true);
    },
    []
  );

  const filtersContent = (
    <ExplorerFilters
      regions={regions}
      onFilterChange={handleFilterChange}
      isLoading={isPending}
    />
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Stats Overview */}
      <StatCards stats={data.stats} filteredCount={data.filteredCount} isLoading={isPending} />

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters - Desktop Sidebar */}
        <div className="hidden lg:block">
          <Card className="sticky top-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FilterIcon className="h-4 w-4" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {filtersContent}
              
              {/* Calculate Button */}
              <div className="pt-4 border-t space-y-2">
                <Button
                  onClick={handleCalculate}
                  disabled={isPending}
                  className="w-full"
                  variant={filtersChanged ? 'default' : 'secondary'}
                >
                  {isPending ? (
                    <>
                      <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                      Calculating...
                    </>
                  ) : filtersChanged ? (
                    <>
                      <CalculatorIcon className="mr-2 h-4 w-4" />
                      Calculate Distribution
                    </>
                  ) : (
                    <>
                      <RefreshCwIcon className="mr-2 h-4 w-4" />
                      Recalculate
                    </>
                  )}
                </Button>
                {filtersChanged && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                    Filters changed — click to update charts
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mobile Filters Sheet */}
        <div className="lg:hidden flex justify-end gap-2">
          <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm">
                <FilterIcon className="h-4 w-4 mr-2" />
                Filters
                {filtersChanged && (
                  <span className="ml-2 h-2 w-2 rounded-full bg-amber-500" />
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                {filtersContent}
                <Button
                  onClick={() => {
                    handleCalculate();
                    setMobileFiltersOpen(false);
                  }}
                  disabled={isPending}
                  className="w-full"
                >
                  {isPending ? (
                    <>
                      <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <CalculatorIcon className="mr-2 h-4 w-4" />
                      Calculate
                    </>
                  )}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          
          {/* Mobile Calculate Button */}
          <Button
            onClick={handleCalculate}
            disabled={isPending}
            size="sm"
            variant={filtersChanged ? 'default' : 'secondary'}
          >
            {isPending ? (
              <Loader2Icon className="h-4 w-4 animate-spin" />
            ) : (
              <CalculatorIcon className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Charts */}
        <div className="lg:col-span-3 space-y-6 relative">
          {/* Loading Overlay */}
          {isPending && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
              <div className="flex flex-col items-center gap-3 p-6 rounded-lg bg-card border shadow-lg">
                <Loader2Icon className="h-8 w-8 animate-spin text-primary" />
                <div className="text-center">
                  <p className="font-medium">Calculating Distribution</p>
                  <p className="text-sm text-muted-foreground">
                    Analyzing wedding data...
                  </p>
                </div>
              </div>
            </div>
          )}

          <Tabs defaultValue="distribution" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="distribution">Total Cost Distribution</TabsTrigger>
              <TabsTrigger value="categories">By Category</TabsTrigger>
              <TabsTrigger value="items">By Item</TabsTrigger>
            </TabsList>

            <TabsContent value="distribution" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Wedding Cost Distribution</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Showing {data.filteredCount.toLocaleString()} weddings
                    {data.stats.avgTotalSpend > 0 && (
                      <> • Average: ${data.stats.avgTotalSpend.toLocaleString()}</>
                    )}
                  </p>
                </CardHeader>
                <CardContent>
                  <TotalSpendHistogram 
                    samples={data.samples} 
                    isLoading={isPending}
                    onBucketClick={handleBucketClick}
                  />
                  <p className="text-xs text-muted-foreground text-center mt-4">
                    Click on any bar to view individual wedding budgets in that range
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="categories" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Spending by Category</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Average spending breakdown across all wedding categories
                  </p>
                </CardHeader>
                <CardContent>
                  <CategoryBreakdownChart
                    categoryBreakdown={data.stats.categoryBreakdown}
                    isLoading={isPending}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="items" className="mt-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Item Cost Distribution</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Select an item to see its cost distribution
                      </p>
                    </div>
                    <select
                      value={selectedItem}
                      onChange={(e) => setSelectedItem(e.target.value)}
                      className="text-sm border rounded-md px-3 py-1.5 bg-background"
                    >
                      {itemNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                </CardHeader>
                <CardContent>
                  <ItemDistributionChart
                    samples={data.samples}
                    itemName={selectedItem}
                    isLoading={isPending}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

        </div>
      </div>

      {/* Wedding Detail Modal */}
      <WeddingListModal
        open={weddingModalOpen}
        onOpenChange={setWeddingModalOpen}
        weddings={selectedBucket?.samples || []}
        bucketRange={selectedBucket ? { min: selectedBucket.min, max: selectedBucket.max } : null}
      />
    </div>
  );
}

